// "Looking for a match" client state. The server owns the registry
// (ws/lfg.js); this mirrors our own entry, re-declares it after a
// reconnect (a drop removes us server-side), and fans out the offer
// lifecycle: matched → accepted (partner) → start, or cancelled.
import { connectWebSocket, send, on, onServerReconnect } from '../serverClient';
import { getPref, setPref } from '../persistentPrefs';

const PREF_KEY = 'vk-lfg-prefs';

const RULE_KEYS = ['draft', 'sealed', 'constructed', 'sandbox'];
let state = { active: false, prefs: getPref(PREF_KEY, null) || { modes: ['draft', 'sealed', 'constructed'], stakes: 'either', format: 'either', rules: 'manual', pod: { draft: 'either', sealed: 'either' } } };

// Panel prefs → server registry shape. Constructed folds into its stakes
// (ranked / casual); format 'either' means both, as does an unset pod size.
export function toServerPrefs(p) {
  const modes = [];
  for (const m of p.modes || []) {
    if (m === 'constructed') modes.push(...(p.stakes === 'ranked' ? ['ranked'] : p.stakes === 'casual' ? ['casual'] : ['ranked', 'casual']));
    else modes.push(m);
  }
  const formats = p.format === 'peasant' ? ['peasant'] : p.format === 'constructed' ? ['constructed'] : ['constructed', 'peasant'];
  // One rules choice covers every mode; the server still keys it per mode.
  const v = typeof p.rules === 'string' ? p.rules : (p.automations ? 'automated' : 'manual');
  const list = v === 'either' ? ['manual', 'automated'] : [v];
  const rules = {};
  for (const k of RULE_KEYS) rules[k] = list;
  const sets = { draft: p.sets?.draft || [], sealed: p.sets?.sealed || [] };
  const seats = (v) => (v === '2' ? [2] : v === '4' ? [4] : [2, 4]);
  const pods = { draft: seats(p.pod?.draft), sealed: seats(p.pod?.sealed) };
  return { modes, formats, rules, sets, pods };
}
const listeners = new Set();
const matchedListeners = new Set();
let wired = false;

function emit() {
  for (const cb of listeners) { try { cb(state); } catch {} }
}

function fire(type, data) {
  for (const cb of matchedListeners) { try { cb(type, data); } catch {} }
}

function wire() {
  if (wired) return;
  wired = true;
  on('lfg:status', (data) => {
    state = { ...state, active: !!data?.active, since: data?.since || null, widened: !!data?.widened, table: data?.table || null, error: null };
    emit();
  });
  on('lfg:error', (data) => {
    state = { ...state, active: false, error: data?.error || 'Could not start looking' };
    emit();
  });
  on('lfg:matched', (data) => {
    state = { ...state, active: false };
    emit();
    fire('matched', data);
  });
  on('lfg:accepted', (data) => fire('accepted', data));
  on('lfg:start', (data) => fire('start', data));
  // The server re-lists whoever was still willing; lfg:status follows.
  on('lfg:cancelled', (data) => {
    state = { ...state, active: !!data?.requeued };
    emit();
    fire('cancelled', data);
  });
  onServerReconnect(() => { if (state.active) send('lfg:join', toServerPrefs(state.prefs)); });
}

export function getState() { return state; }

export function subscribe(cb) {
  wire();
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** cb(type, data) for 'matched' | 'accepted' | 'start' | 'cancelled'. */
export function onMatchEvent(cb) {
  wire();
  matchedListeners.add(cb);
  return () => matchedListeners.delete(cb);
}

/** paymentMethod: 'keystone' | 'coins' for a priced event, else omitted. */
export function accept(paymentMethod = null) { send('lfg:accept', paymentMethod ? { paymentMethod } : {}); }
export function decline() { send('lfg:decline', {}); }

export async function start(prefs) {
  wire();
  state = { ...state, prefs, error: null };
  setPref(PREF_KEY, prefs);
  emit();
  await connectWebSocket();
  send('lfg:join', toServerPrefs(prefs));
}

export async function stop() {
  wire();
  await connectWebSocket();
  send('lfg:leave', {});
  state = { ...state, active: false };
  emit();
}
